import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import merge from 'lodash/merge';
import { CustomPoint, PieChartProps } from './pieChart.helpers';
import {
  usePieChartDefaultOptions,
} from './usePieChartDefaultOptions';
import { useHighchartsSharedSetup } from '../utils/useHighchartsSharedSetup';

export interface PieChartRef {
  getChart: () => Highcharts.Chart | undefined;
  handleLegendHover: (pointName: string, isHovering: boolean) => void;
}

export const PieChart = forwardRef<PieChartRef, PieChartProps>(({
  data,
  hideTooltip = false,
  bigLegend = false,
  chartWidth,
  chartHeight,
  hideLegend = false,
  onPointHover,
}, ref) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const tooltipVisibility = useRef<boolean>(false);
  useHighchartsSharedSetup();

  useImperativeHandle(ref, () => ({
      getChart: () => chartComponentRef.current?.chart,
      handleLegendHover: (pointName: string, isHovering: boolean) => {
        const chart = chartComponentRef.current?.chart;
        if (!chart) return;

        chart.series[0].points.forEach((point: CustomPoint) => {
          if (point.name.toLowerCase() === pointName.toLowerCase()) {
            if (isHovering) {
              tooltipVisibility.current = true;
              point.onMouseOver();
              chart.tooltip.refresh(point);
            } else {
              tooltipVisibility.current = false;
              point.onMouseOut();
              point.setState('');
              chart.tooltip.hide(0);
            }
          }
        });
      }
  }));
  const pieChartDefaultOptions = usePieChartDefaultOptions(
    data.length,
    chartWidth || null,
    chartHeight || null,
    hideTooltip,
    hideLegend,
    bigLegend,
    onPointHover,
    tooltipVisibility,
  );
  const dataOptions: Highcharts.Options = {
    series: [
        {
            type: 'pie',
            innerSize: '60%',
            data,
        },
    ],
  };

  const chartOptions = merge(pieChartDefaultOptions, dataOptions);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: chartWidth ?? undefined, height: chartHeight ?? undefined }}>
      <HighchartsReact
          highcharts={Highcharts}
          constructorType="chart"
          options={chartOptions}
          ref={chartComponentRef}
      />
    </div>
  );
});

PieChart.displayName = 'PieChart';
